import React, { useEffect, useState } from "react";
import { AppShell } from "../../components/Sidebar";
import { LegendDots, StatusBadge } from "../../components/StatusBadge";
import { api } from "../../lib/api";
import { BookOpen, Trophy, Clock, Shield, Search } from "lucide-react";

export default function ParentDashboard() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/parent/dashboard").then((r) => setData(r.data)); }, []);

  if (!data) return <AppShell role="parent"><p className="text-slate-500">Loading…</p></AppShell>;
  const { child, summary, modules } = data;
  const filtered = modules.filter((m) => m.module_name.toLowerCase().includes(search.toLowerCase()));
  const initials = child.first_name?.[0] + (child.last_name?.[0] || "");

  return (
    <AppShell role="parent">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Parent Portal</h1>
          <p className="mt-1 text-slate-500">A read-only view of your child's learning journey.</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700">
          <Shield size={12} /> Read-only access
        </span>
      </div>

      <div className="mt-7 rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
        <div className="grid items-center gap-5 lg:grid-cols-[2fr_1fr_1fr_1fr]">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-100 text-2xl font-bold text-indigo-700">{initials.toUpperCase()}</div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{child.full_name}
                <StatusBadge value="Active" className="ml-2 align-middle" withDot />
              </h2>
              <p className="mt-1 text-sm text-slate-500">{child.class_label}</p>
              <p className="text-sm text-slate-500">{child.school_name}</p>
              <p className="text-xs text-slate-400">Last active: {child.last_active}</p>
            </div>
          </div>
          <SummaryStat icon={BookOpen} color="bg-violet-100 text-violet-600" label="Modules Progress" value={`${summary.modules_completed} / ${summary.total_modules}`} sub="Completed" progress={summary.progress_percent} />
          <SummaryStat icon={Clock} color="bg-blue-100 text-blue-600" label="Total Time Spent" value={summary.total_time_spent} sub="Across all modules" />
          <SummaryStat icon={Trophy} color="bg-emerald-100 text-emerald-600" label="Average Quiz Score" value={`${summary.average_quiz_score}%`} sub="Across completed modules" />
        </div>
      </div>

      <div className="mt-7 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{child.first_name}'s Learning Progress</h3>
            <p className="text-sm text-slate-500">Track performance across all modules.</p>
          </div>
          <LegendDots />
        </div>

        <div className="relative mt-5 max-w-sm">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search modules…" className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th rowSpan={2} className="py-3 pr-4 align-bottom">#</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">Chapter Name</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">Subject</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">Status</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">Quiz Score</th>
                <th colSpan={3} className="pb-1 text-center font-semibold text-slate-700">Understanding Level</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">Last Accessed</th>
              </tr>
              <tr className="border-b border-slate-100 text-xs font-semibold text-slate-500">
                <th className="py-2 pr-4">Conceptual</th>
                <th className="py-2 pr-4">Applied</th>
                <th className="py-2 pr-4">Theoretical</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => (
                <tr key={m.module_id} className="border-b border-slate-50">
                  <td className="py-4 pr-4 text-slate-500">{i + 1}</td>
                  <td className="py-4 pr-4 font-medium text-slate-900">{m.module_name}</td>
                  <td className="py-4 pr-4 text-slate-700">{m.subject}</td>
                  <td className="py-4 pr-4"><StatusBadge value={m.status} /></td>
                  <td className="py-4 pr-4 font-semibold text-slate-700">{m.quiz_score != null ? `${m.quiz_score}%` : "—"}</td>
                  <td className="py-4 pr-4"><StatusBadge value={m.conceptual} withDot compact /></td>
                  <td className="py-4 pr-4"><StatusBadge value={m.applied} withDot compact /></td>
                  <td className="py-4 pr-4"><StatusBadge value={m.theoretical} withDot compact /></td>
                  <td className="py-4 pr-4 text-slate-600">{m.last_accessed || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

const SummaryStat = ({ icon: Icon, color, label, value, sub, progress }) => (
  <div>
    <div className="flex items-start gap-3">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}><Icon size={20} /></div>
      <div className="flex-1">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="mt-0.5 text-xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{sub}</p>
        {progress != null && (
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-indigo-600" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    </div>
  </div>
);
